import useEmblaCarousel from "embla-carousel-react";
import React from "react";
import { VisuallyHidden } from "react-aria-components";
import { t } from "ttag";

import { TestimonialCarouselBlock } from "@reactivated";

import { Clickable, IconButton } from "../Clickables";
import { ThemedModule } from "../ThemedModule";
import { TestimonialCard } from "./TestimonialCard";

import styles from "./TestimonialCarousel.module.scss";

export const CARD_SHAPE = {
    BOX: "box",
    SPEECH_BUBBLE: "speech-bubble",
} as const;

export const TestimonialCarousel = ({
    value,
}: {
    value: TestimonialCarouselBlock;
}) => {
    const [emblaRef, emblaApi] = useEmblaCarousel({
        loop: true,
        align: "center",
    });
    const emblaSubscribe = React.useCallback(
        (callback: () => void) => {
            if (!emblaApi) return () => {};
            emblaApi.on("select", callback);
            emblaApi.on("reInit", callback);
            return () => {
                emblaApi.off("select", callback);
                emblaApi.off("reInit", callback);
            };
        },
        [emblaApi],
    );
    const selectedIndex = React.useSyncExternalStore(
        emblaSubscribe,
        () => emblaApi?.selectedScrollSnap() ?? 0,
        () => 0,
    );

    const slideCount = value.testimonials?.length ?? 0;

    return (
        <ThemedModule theme={value.module_theme}>
            <section className={styles.root}>
                <div
                    role="group"
                    aria-roledescription="carousel"
                    aria-label={t`Testimonials`}
                    className={styles.carousel}
                >
                    <div className={styles.viewport} ref={emblaRef}>
                        <div className={styles.container}>
                            {value.testimonials?.map((testimonial, index) => (
                                <div
                                    key={index}
                                    role="group"
                                    aria-roledescription="slide"
                                    aria-label={t`Slide ${index + 1} of ${slideCount}`}
                                    className={styles.slide}
                                >
                                    <TestimonialCard
                                        value={testimonial}
                                        theme={value.card_color_theme}
                                        cardShape={value.card_shape}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className={styles.controls}>
                        <IconButton
                            icon="caret"
                            label={t`Previous item`}
                            onPress={() => emblaApi?.scrollPrev()}
                        />
                        <IconButton
                            icon="caret"
                            label={t`Next item`}
                            onPress={() => emblaApi?.scrollNext()}
                        />
                    </div>

                    <div
                        className={styles.dotsWrapper}
                        role="group"
                        aria-label={t`Choose slide to display`}
                    >
                        {value.testimonials?.map((_, i) => (
                            <Clickable
                                key={i}
                                className={styles.dot}
                                onPress={() => emblaApi?.scrollTo(i)}
                                data-active={
                                    i === selectedIndex ? true : undefined
                                }
                            >
                                <VisuallyHidden>{t`Slide ${i + 1} of ${slideCount}`}</VisuallyHidden>
                            </Clickable>
                        ))}
                    </div>
                </div>
            </section>
        </ThemedModule>
    );
};
